import { httpClient } from "./client";
import type { TournamentApi, TournamentEventsPage } from "./interfaces";
import type {
  TournamentCreate,
  TournamentPublishReadinessResponse,
  TournamentUpdate,
  TournamentResponse,
  DrawCreate,
  DrawUpdate,
  DrawResponse,
  DrawEntryResponse,
  TournamentPlayerEntryResponse,
  TournamentPlayerRegistrationResponse,
  TournamentPlayerEntryActionResponse,
  TournamentEntryActionResponse,
  TournamentEntryRejectionRequest,
  TournamentMockPlayerListResponse,
  TournamentMockRegistrationCreate,
  TournamentRegistrationCreate,
  TournamentRegistrationCheckoutCreate,
  TournamentRegistrationCheckoutResponse,
  TournamentRegistrationCheckoutStatusResponse,
  BracketMatchResponse,
  TournamentQualificationResponse,
  TournamentQualificationOverrideCommand,
  UUID,
} from "../types";
import type { TournamentPlayerEntryPageResponse } from "../types/generated";
import type {
  Schema,
  BrowseTournamentsPage,
  StandingsResponse,
  EntryCreate,
  EventEligibilityVerdict,
  SoloEntryCreate,
  PartnerCandidate,
  PairingProposalCreate,
  PairingRespond,
  PairingResponse,
  GuardianConsentConfirmResponse,
  GuardianConsentAcceptance,
  GuardianConsentPreviewResponse,
} from "../types/generated";

type TournamentPublicArchiveResponse =
  Schema["TournamentPublicArchiveResponse"];
type TournamentPublicArchiveReleaseResponse =
  Schema["TournamentPublicArchiveReleaseResponse"];
type TournamentOwnArchiveCertificateResponse =
  Schema["TournamentOwnArchiveCertificateResponse"];

export const tournamentApi: TournamentApi = {
  create: (facilityId: UUID, data: TournamentCreate) =>
    httpClient.post<TournamentResponse>(
      `/tournaments/facilities/${facilityId}`,
      data,
    ),

  list: (facilityId: UUID, status?: string) => {
    const qs = status ? `?status=${status}` : "";
    return httpClient.get<TournamentResponse[]>(
      `/tournaments/facilities/${facilityId}${qs}`,
    );
  },

  browse: (params) => {
    const search = new URLSearchParams();
    if (params?.q) search.set("q", params.q);
    if (params?.status) search.set("status", params.status);
    if (params?.tournament_type)
      search.set("tournament_type", params.tournament_type);
    if (params?.event_mode) search.set("event_mode", params.event_mode);
    if (params?.event_grade) search.set("event_grade", params.event_grade);
    if (params?.city) search.set("city", params.city);
    if (params?.facility_id) search.set("facility_id", params.facility_id);
    if (params?.starts_after) search.set("starts_after", params.starts_after);
    if (params?.lat !== undefined) search.set("lat", String(params.lat));
    if (params?.lng !== undefined) search.set("lng", String(params.lng));
    if (params?.radius_km !== undefined)
      search.set("radius_km", String(params.radius_km));
    if (params?.official !== undefined)
      search.set("official", String(params.official));
    if (params?.limit !== undefined) search.set("limit", String(params.limit));
    if (params?.offset !== undefined)
      search.set("offset", String(params.offset));
    const qs = search.toString();
    return httpClient.get<BrowseTournamentsPage>(
      qs ? `/tournaments?${qs}` : `/tournaments`,
    );
  },

  get: (tournamentId: UUID) =>
    httpClient.get<TournamentResponse>(`/tournaments/${tournamentId}`),

  // Anonymous-readable PUBLIC detail for the player discovery surface. Distinct
  // from `get` (an operator-callable read) by INTENT — same path today, but the
  // player module binds to this method so the public-read seam is explicit and
  // the id is path-encoded.
  getPublic: (tournamentId: string) =>
    httpClient.get<TournamentResponse>(
      `/tournaments/${encodeURIComponent(tournamentId)}`,
    ),

  listPublicPlayers: (tournamentId: string) =>
    httpClient.get<Schema["TournamentPlayerPublicResponse"][]>(
      `/tournaments/${encodeURIComponent(tournamentId)}/players`,
    ),

  getPublicArchive: (tournamentId: string) =>
    httpClient.get<TournamentPublicArchiveResponse>(
      `/tournaments/${encodeURIComponent(tournamentId)}/archive`,
    ),

  getPublicArchiveRelease: (tournamentId: string) =>
    httpClient.get<TournamentPublicArchiveReleaseResponse>(
      `/tournaments/${encodeURIComponent(tournamentId)}/archive/release`,
    ),

  downloadPublicArchiveReport: (tournamentId: string) =>
    httpClient.getBlob(
      `/tournaments/${encodeURIComponent(tournamentId)}/archive/report.pdf`,
    ),

  listMyArchiveCertificates: (tournamentId: string) =>
    httpClient.get<TournamentOwnArchiveCertificateResponse[]>(
      `/tournaments/${encodeURIComponent(tournamentId)}/archive/certificates/mine`,
    ),

  downloadMyArchiveCertificate: (tournamentId: string, certificateId: string) =>
    httpClient.getBlob(
      `/tournaments/${encodeURIComponent(tournamentId)}/archive/certificates/${encodeURIComponent(certificateId)}.pdf`,
    ),

  // Anonymous-readable PUBLIC events list for a tournament (GET
  // /tournaments/{id}/events → the backend `_page` wrapper {items, total, page,
  // page_size}). `pageSize` maps to the backend's snake_case `page_size`; both
  // params are OMITTED when unset so the server applies its defaults (page=1,
  // page_size=50). The id is path-encoded.
  listEvents: (tournamentId, params) => {
    const search = new URLSearchParams();
    if (params?.page !== undefined) search.set("page", String(params.page));
    if (params?.pageSize !== undefined)
      search.set("page_size", String(params.pageSize));
    const qs = search.toString();
    return httpClient.get<TournamentEventsPage>(
      `/tournaments/${encodeURIComponent(tournamentId)}/events${
        qs ? `?${qs}` : ""
      }`,
    );
  },

  getEventCatalog: (tournamentId: string) =>
    httpClient.get<Schema["TournamentEventCatalog"]>(
      `/tournaments/${encodeURIComponent(tournamentId)}/event-catalog`,
    ),

  getPublishReadiness: (tournamentId: UUID) =>
    httpClient.get<TournamentPublishReadinessResponse>(
      `/tournaments/${tournamentId}/publish-readiness`,
    ),

  publish: (tournamentId: UUID) =>
    httpClient.post<TournamentResponse>(
      `/tournaments/${tournamentId}/publish`,
      {},
    ),

  update: (tournamentId: UUID, data: TournamentUpdate) =>
    httpClient.patch<TournamentResponse>(`/tournaments/${tournamentId}`, data),

  delete: (tournamentId: UUID) =>
    httpClient.delete<void>(`/tournaments/${tournamentId}`),

  cancel: (tournamentId: UUID) =>
    httpClient.post<void>(`/tournaments/${tournamentId}/cancel`, {}),

  createDraw: (tournamentId: UUID, data: DrawCreate) =>
    httpClient.post<DrawResponse>(`/tournaments/${tournamentId}/draws`, data),

  updateDraw: (drawId: UUID, data: DrawUpdate) =>
    httpClient.patch<DrawResponse>(`/tournaments/draws/${drawId}`, data),

  listDraws: (tournamentId: UUID) =>
    httpClient.get<DrawResponse[]>(`/tournaments/${tournamentId}/draws`),

  registerEntry: (drawId: UUID, data: TournamentRegistrationCreate) =>
    httpClient.post<TournamentPlayerRegistrationResponse>(
      `/tournaments/draws/${encodeURIComponent(drawId)}/register`,
      data,
    ),

  mockRegisterEntry: (drawId: UUID, data: TournamentMockRegistrationCreate) =>
    httpClient.post<DrawEntryResponse>(
      `/tournaments/draws/${drawId}/entries/mock`,
      data,
    ),

  listMockRegistrationPlayers: (tournamentId: UUID, search?: string) => {
    const params = new URLSearchParams();
    if (search?.trim()) {
      params.set("search", search.trim());
    }
    const qs = params.toString();
    return httpClient.get<TournamentMockPlayerListResponse>(
      `/tournaments/${tournamentId}/mock-players${qs ? `?${qs}` : ""}`,
    );
  },

  createRegistrationCheckout: (
    drawId: UUID,
    data: TournamentRegistrationCheckoutCreate,
  ) =>
    httpClient.post<TournamentRegistrationCheckoutResponse>(
      `/tournaments/draws/${drawId}/registration-checkout`,
      data,
    ),

  getRegistrationCheckoutStatus: (registrationCheckoutId: UUID) =>
    httpClient.get<TournamentRegistrationCheckoutStatusResponse>(
      `/tournaments/registration-checkouts/${registrationCheckoutId}`,
    ),

  approveEntry: (entryId: UUID) =>
    httpClient.post<DrawEntryResponse>(
      `/tournaments/entries/${entryId}/approve`,
      {},
    ),

  rejectEntry: (entryId: UUID, data?: TournamentEntryRejectionRequest) =>
    httpClient.post<TournamentEntryActionResponse>(
      `/tournaments/entries/${entryId}/reject`,
      data ?? {},
    ),

  withdrawEntry: (entryId: UUID) =>
    httpClient.delete<TournamentPlayerEntryActionResponse>(
      `/tournaments/entries/${entryId}`,
    ),

  generateBracket: (drawId: UUID) =>
    // Backend POST returns a bare array, but defensively unwrap a `matches`
    // envelope if the BFF ever rewrites to the public_event_router path,
    // which returns `{draw, tournament_type, matches: BracketMatch[]}`.
    httpClient
      .post<unknown>(`/tournaments/draws/${drawId}/bracket`)
      .then((v) => {
        if (
          v &&
          typeof v === "object" &&
          Array.isArray((v as { matches?: unknown }).matches)
        ) {
          return (v as { matches: BracketMatchResponse[] }).matches;
        }
        return Array.isArray(v) ? (v as BracketMatchResponse[]) : [];
      }),

  getBracket: (drawId: UUID) =>
    // Backend GET /draws/{id}/bracket (the public_event_router path the BFF
    // rewrites this URL to) returns `{draw, tournament_type, matches: BracketMatch[]}`
    // — not a bare array. Unwrap `matches` inline; fall back to a bare array
    // or empty list so any future divergence degrades gracefully.
    httpClient
      .get<unknown>(`/tournaments/draws/${drawId}/bracket`)
      .then((v) => {
        if (
          v &&
          typeof v === "object" &&
          Array.isArray((v as { matches?: unknown }).matches)
        ) {
          return (v as { matches: BracketMatchResponse[] }).matches;
        }
        return Array.isArray(v) ? (v as BracketMatchResponse[]) : [];
      }),

  // Anonymous-readable round-robin standings for a draw (GET
  // /tournaments/draws/{id}/standings → StandingsResponse). Unlike getBracket
  // (which the BFF may wrap in a {matches} envelope), standings is the BARE
  // StandingsResponse object — return it verbatim, no unwrap, no reshaping.
  // The id is encodeURIComponent'd so a stray slash/space can't corrupt the path.
  getStandings: (drawId: UUID) =>
    httpClient.get<StandingsResponse>(
      `/tournaments/draws/${encodeURIComponent(drawId)}/standings`,
    ),

  getQualification: (drawId: UUID) =>
    httpClient.get<TournamentQualificationResponse>(
      `/tournaments/draws/${encodeURIComponent(drawId)}/qualification`,
    ),

  overrideQualification: (
    drawId: UUID,
    command: TournamentQualificationOverrideCommand,
  ) =>
    httpClient.post<TournamentQualificationResponse>(
      `/tournaments/draws/${encodeURIComponent(drawId)}/qualification/override`,
      command,
    ),

  // The caller's draw entries across EVERY tournament (GET
  // /users/me/registrations → TournamentPlayerEntryPageResponse), SERVER-SCOPED to the auth
  // user and SERVER-ORDERED (newest-first) — the client never re-sorts. Takes
  // NO tournamentId: the cross-tournament variant of getTournamentRegistrationsMine
  // (the per-tournament read), backing the player's "My entries" history list.
  getMyTeamEntries: (params = {}) => {
    const search = new URLSearchParams();
    if (params.limit != null) search.set("limit", String(params.limit));
    if (params.offset != null) search.set("offset", String(params.offset));
    if (params.team_entry_id) search.set("team_entry_id", params.team_entry_id);
    const query = search.toString();
    return httpClient.get<
      import("../types/generated").TournamentPlayerTeamEntryPageResponse
    >(`/users/me/tournament-team-entries${query ? `?${query}` : ""}`);
  },
  withdrawTeamEntry: (entryId) =>
    httpClient.delete<void>(`/team-entries/${encodeURIComponent(entryId)}`),
  getTeamMatches: (drawId, params = {}) => {
    const search = new URLSearchParams();
    if (params.page != null) search.set("page", String(params.page));
    if (params.page_size != null)
      search.set("page_size", String(params.page_size));
    const query = search.toString();
    return httpClient.get<
      import("../types/generated").TournamentTeamMatchListResponse
    >(
      `/draws/${encodeURIComponent(drawId)}/team-matches${query ? `?${query}` : ""}`,
    );
  },
  getMyRegistrations: (params = {}, signal) => {
    const search = new URLSearchParams();
    if (params.limit != null) search.set("limit", String(params.limit));
    if (params.offset != null) search.set("offset", String(params.offset));
    if (params.entry_id != null) search.set("entry_id", params.entry_id);
    const query = search.toString();
    return httpClient.get<TournamentPlayerEntryPageResponse>(
      `/users/me/registrations${query ? `?${query}` : ""}`,
      { signal },
    );
  },

  // EPIC 1.2 multi-event registration. Per-event eligibility verdicts for the
  // signed-in player (GET /tournaments/{id}/eligibility → EventEligibilityVerdict[]
  // in the backend's deterministic order). Eligibility logic is BACKEND-owned: the
  // client returns the list VERBATIM and never recomputes — the FE greys an
  // ineligible row and shows reasons[0].message as-is. The id is encodeURIComponent'd
  // so a stray slash/space can't corrupt the path; a non-2xx REJECTS.
  getTournamentEligibility: (tournamentId: UUID) =>
    httpClient.get<EventEligibilityVerdict[]>(
      `/tournaments/${encodeURIComponent(tournamentId)}/eligibility`,
    ),

  // EPIC 1.2. The caller's entries SCOPED to one tournament (GET
  // /tournaments/{id}/registrations/mine → TournamentPlayerEntryResponse[] with LIVE status +
  // hydrated event/draw/partner names), SERVER-SCOPED to the token user. The
  // tournament-scoped variant of getMyRegistrations (the cross-tournament list);
  // it replaces the singular getMyRegistration (first-entry-any-status) the GREEN
  // migration deletes. The id is encodeURIComponent'd; a non-2xx REJECTS.
  getTournamentRegistrationsMine: (tournamentId: UUID) =>
    httpClient.get<TournamentPlayerEntryResponse[]>(
      `/tournaments/${encodeURIComponent(tournamentId)}/registrations/mine`,
    ),

  // The player REGISTER flow (S1). POST /events/{event_id}/register on the
  // event_router (app-root mounted — NOT a /tournaments-prefixed path), body the
  // EntryCreate VERBATIM ({ draw_id, expected_quote, partner_id?,
  // payment_method_id? }) → the
  // created player-safe result (201). MONEY discipline: the body is forwarded as-is —
  // client never computes a fee: expected_quote is the exact server quote the
  // player confirmed, and a free entry simply omits payment_method_id. A non-2xx
  // REJECTS (httpClient
  // throws an ApiError carrying the status), so the panel can never flip
  // "registered" on a failed charge. The id is encodeURIComponent'd so a stray
  // slash/space can't corrupt the path.
  register: (eventId: UUID, body: EntryCreate) =>
    httpClient.post<TournamentPlayerRegistrationResponse>(
      `/events/${encodeURIComponent(eventId)}/register`,
      body,
    ),

  abandonRegistrationAttempt: (drawId: UUID, expectedCheckoutId?: UUID) =>
    httpClient.post<Schema["TournamentDirectAttemptResolutionResponse"]>(
      `/tournaments/draws/${encodeURIComponent(drawId)}/registration-attempt/abandon${expectedCheckoutId ? `?expected_checkout_id=${encodeURIComponent(expectedCheckoutId)}` : ""}`,
    ),

  // ---------------------------------------------------------------------------
  // Partner-pairing surface (EPIC 1.4 — solo entry, partner finder, pairing
  // inbox). Thin transport: each method forwards the aliased DTO VERBATIM and
  // returns the parsed response unchanged. No business logic — eligibility,
  // pricing, role, and the candidate set are ALL backend-owned; the client
  // never derives, defaults, or reshapes them. A non-2xx REJECTS (httpClient
  // throws an ApiError carrying the status) — never swallowed into a fake
  // success / empty list. Path ids are encodeURIComponent'd.
  // ---------------------------------------------------------------------------

  // Enter a doubles/mixed draw SOLO ("I'll find a partner later"). POST
  // /tournaments/draws/{id}/entries/solo, body the SoloEntryCreate as-is (free
  // entry sends the confirmed free expected_quote; a paid one also adds
  // payment_method_id) → the created partner_wanted TournamentPlayerEntryResponse.
  enterSolo: (drawId: string, body: SoloEntryCreate) =>
    httpClient.post<TournamentPlayerEntryResponse>(
      `/tournaments/draws/${encodeURIComponent(drawId)}/entries/solo`,
      body,
    ),

  // PUBLIC guardian-consent preview/confirmation (#1684 / #1692). Both use
  // body-only token-authenticated POSTs so the bearer token never appears in a
  // URL, browser history, referrer, or BFF/backend query log. The guardian may
  // be logged out, so these ride the BFF's tiny public-POST allowlist (no
  // session, CSRF-exempt: see bff/src/proxy.ts + bff/src/csrf.ts). The backend
  // projects confirmation down to ONLY child/event/payment facts — never entrant
  // internals or financial routing. The client forwards the token verbatim and
  // never derives any amount or eligibility.
  previewGuardianConsent: (token: string) =>
    httpClient.post<GuardianConsentPreviewResponse>(
      `/tournaments/entries/guardian-consent/preview`,
      { token },
    ),

  confirmGuardianConsent: (
    token: string,
    acceptances?: GuardianConsentAcceptance[],
  ) =>
    httpClient.post<GuardianConsentConfirmResponse>(
      `/tournaments/entries/guardian-consent/confirm`,
      acceptances?.length ? { token, acceptances } : { token },
    ),

  // The information-rich partner finder (GET
  // /tournaments/draws/{id}/partner-candidates?q=&limit= → PartnerCandidate[]).
  // The request is ALWAYS issued, even with an empty q (the server treats empty
  // as "the whole linkable set" — never short-circuit client-side). `limit` is
  // omitted when unset so the server applies its default. The list is returned
  // verbatim; a non-2xx REJECTS (NOT swallowed into []).
  getPartnerCandidates: (
    drawId: string,
    params: { q?: string; limit?: number },
  ) => {
    const search = new URLSearchParams();
    // q is always present (empty string included) — the empty-query finder.
    search.set("q", params.q ?? "");
    if (params.limit !== undefined) search.set("limit", String(params.limit));
    return httpClient.get<PartnerCandidate[]>(
      `/tournaments/draws/${encodeURIComponent(drawId)}/partner-candidates?${search.toString()}`,
    );
  },

  // Propose a pairing to a candidate already entered in the draw. POST
  // /tournaments/draws/{id}/pairings, body { partner_entry_id } (the candidate's
  // own entry_id, fed straight from a PartnerCandidate) → the proposed
  // PairingResponse (viewer_role "proposer").
  proposePairing: (drawId: string, body: PairingProposalCreate) =>
    httpClient.post<PairingResponse>(
      `/tournaments/draws/${encodeURIComponent(drawId)}/pairings`,
      body,
    ),

  // The caller's pairing inbox for a draw (GET
  // /tournaments/draws/{id}/pairings/mine → PairingResponse[], newest-first,
  // SERVER-SCOPED to the token user). Each pairing carries its own
  // server-computed viewer_role; the client returns them verbatim and never
  // derives the role. A non-2xx REJECTS (NOT swallowed into an empty list).
  getMyPairings: (drawId: string) =>
    httpClient.get<PairingResponse[]>(
      `/tournaments/draws/${encodeURIComponent(drawId)}/pairings/mine`,
    ),
  getMyPairingRequests: (params = {}) => {
    const search = new URLSearchParams();
    if (params.limit != null) search.set("limit", String(params.limit));
    if (params.offset != null) search.set("offset", String(params.offset));
    if (params.pairing_id) search.set("pairing_id", params.pairing_id);
    const query = search.toString();
    return httpClient.get<
      import("../types/generated").TournamentPairingRequestPageResponse
    >(`/users/me/tournament-pairings${query ? `?${query}` : ""}`);
  },
  resumeRegistrationCardSetup: (checkoutId, paymentMethodId) =>
    httpClient.post<TournamentPlayerEntryResponse>(
      `/tournaments/registration-checkouts/${encodeURIComponent(checkoutId)}/card-setup/resume`,
      { payment_method_id: paymentMethodId },
    ),
  releaseRegistrationCardSetup: (checkoutId) =>
    httpClient.post<Schema["TournamentDirectAttemptResolutionResponse"]>(
      `/tournaments/registration-checkouts/${encodeURIComponent(checkoutId)}/card-setup/release`,
    ),
  resumeRegistrationCheckout: (checkoutId) =>
    httpClient.post<TournamentRegistrationCheckoutResponse>(
      `/tournaments/registration-checkouts/${encodeURIComponent(checkoutId)}/resume`,
    ),
  getMyRegistrationPayments: (params = {}) => {
    const search = new URLSearchParams();
    if (params.limit != null) search.set("limit", String(params.limit));
    if (params.offset != null) search.set("offset", String(params.offset));
    if (params.payment_id) search.set("payment_id", params.payment_id);
    const query = search.toString();
    return httpClient.get<
      import("../types/generated").TournamentRegistrationPaymentPageResponse
    >(`/users/me/tournament-payments${query ? `?${query}` : ""}`);
  },

  // Accept or decline a pairing — PAIRING-scoped (NOT draw-scoped): POST
  // /tournaments/pairings/{id}/respond, body { accept } as-is → the updated
  // PairingResponse. Per-entity auth (only the linked partner) is enforced
  // server-side; a 403 REJECTS.
  respondPairing: (pairingId: string, body: PairingRespond) =>
    httpClient.post<PairingResponse>(
      `/tournaments/pairings/${encodeURIComponent(pairingId)}/respond`,
      body,
    ),
};
